import { useEffect } from "react";
import { useMemo, useState } from "react";
import { useMsal } from "@azure/msal-react";
import { setTimeout } from "worker-timers";
import { Stack, IconButton } from "@fluentui/react";
import DOMPurify from "dompurify";
import { MarkdownRenderer } from "../MarkdownRenderer";

import styles from "./Answer.module.css";

import { getToken } from "../../authConfig";
import { handleError } from "../../toaster";
import { CitationLink, ChatAppResponse, getCitationFilePath, ExportRequest, exportAnswer, getSpeechApi } from "../../api";
import { parseAnswerToHtml } from "./AnswerParser";
import { AnswerIcon } from "./AnswerIcon";
import { AnswerOptionsPanel } from "./AnswerOptionsPanel";

interface Props {
    index: number;
    conversationId: string;
    question?: string;
    answer: ChatAppResponse;
    isAudioPlaying: boolean;
    onToggleAudioPlay: (index: number, speechUrl: string) => void;
    cachedAudioUrl: string | null;
    onCacheAudioUrl: (index: number, url: string) => void;
    isSelected?: boolean;
    isStreaming: boolean;
    onCitationClicked: (filePath: string, sourcePath: string, pageNumber: string) => void;
    onThoughtProcessClicked: () => void;
    onSupportingContentClicked: () => void;
    onFollowupQuestionClicked?: (question: string) => void;
    showFollowupQuestions?: boolean;
    onSettingsClick: () => void;
    onRegenerateClick: () => void;
    onFeedbackSubmitted: (conversation_id: string, request_id: string, sentiment: string, comments?: string) => void;
    useDarkMode: boolean;
};

export const Answer = ({
    index,
    conversationId,
    question,
    answer,
    isAudioPlaying,
    onToggleAudioPlay,
    cachedAudioUrl,
    onCacheAudioUrl,
    isSelected,
    isStreaming,
    onCitationClicked,
    onThoughtProcessClicked,
    onSupportingContentClicked,
    onFollowupQuestionClicked,
    showFollowupQuestions,
    onSettingsClick,
    onRegenerateClick,
    onFeedbackSubmitted,
    useDarkMode
}: Props) => {
    const client = useMsal().instance;
    const followupQuestions = answer.context.followup_questions;
    const messageContent = answer.message;
    const citation_lookup = answer.context.citation_lookup;
    const request_id = answer.context.request_id;
    const parsedAnswer = useMemo(() => parseAnswerToHtml(messageContent, isStreaming, citation_lookup), [answer]);
    const sanitizedAnswerHtml = DOMPurify.sanitize(parsedAnswer.answerHtml);
    const [isRequestCopied, setIsRequestCopied] = useState(false);
    const [isFeedbackSubmitted, setIsFeedbackSubmitted] = useState(false);
    const [isAudioLoading, setIsAudioLoading] = useState<boolean>(false);

    const toggleAudioPlay = async () => {
        if (cachedAudioUrl) {
            onToggleAudioPlay(index, cachedAudioUrl);
        } else {
            setIsAudioLoading(true);
            const plainText = removeHtmlTags(sanitizedAnswerHtml);
            const token = client ? await getToken(client) : undefined;
            const speechUrl = await getSpeechApi(token, plainText);

            if (!speechUrl) {
                // getSpeechApi already displays an error if there was one
                setIsAudioLoading(false);
                return;
            }

            onCacheAudioUrl(index, speechUrl);
            onToggleAudioPlay(index, speechUrl);
            setIsAudioLoading(false);
        }
    };

    // Set sup colours based on theme mode
    useEffect(() => {
        if (useDarkMode) {
            document.documentElement.style.setProperty("--sup-color", "#ffffff");
            document.documentElement.style.setProperty("--sup-background", "#284f77");
        } else {
            document.documentElement.style.setProperty("--sup-color", "#123bb6");
            document.documentElement.style.setProperty("--sup-background", "#d1dbfa");
        }
    }, [useDarkMode]);

    let citationLinks: CitationLink[] = [];
    if (parsedAnswer.citations.length > 0) {
        parsedAnswer.citations.forEach((x, i) => {
            const path = getCitationFilePath(x);
            const originalFile = x.split("/")[1];
            const pageNumbers = parsedAnswer.pageNumbers[x];
            const sourceFiles = parsedAnswer.sourceFiles[x];
            const linkName = `${originalFile} ${!isNaN(pageNumbers) ? `(Page ${pageNumbers})` : ""}`;

            citationLinks.push({
                key: i,
                sourceFile: sourceFiles,
                pageNumber: pageNumbers,
                title: originalFile,
                onClick: () => onCitationClicked(path, sourceFiles as any, pageNumbers as any),
                label: `${++i}. ${linkName}`,
            });
        });
    };

    const onExportClick = async () => {
        try {
            const request: ExportRequest = {
                request_id: request_id,
                question: question as string,
                answer: sanitizedAnswerHtml,
                citations: citationLinks,
            };
            const token = client ? await getToken(client) : undefined;
            return exportAnswer(token, request);
        } catch (error) {
            handleError(error, "An error occurred while exporting")
        }
    };

    function removeHtmlTags(html: string) {
        return html.replace(/<a\b[^>]*>(.*?)<\/a>/gs, "");
    }; // remove the HTML from the answer for copy to clipboard

    const handleCopyToClipboard = (text: string) => {
        navigator.clipboard.writeText(text);
    };

    const onCopyAnswerClick = async () => {
        try {
            const text = removeHtmlTags(sanitizedAnswerHtml)
            await handleCopyToClipboard(text);
        } catch (error) {
            handleError(error, "An unexpected error occurred while copying answer to clipboard")
        }
    };

    const onCopyRequestIdClick = async (text: string) => {
        try {
            handleCopyToClipboard(text);

            setIsRequestCopied(true);
            setTimeout(() => {
                setIsRequestCopied(false);
            }, 2000); // Change back after 2 seconds

        } catch (error) {
            handleError(error, "An unexpected error occurred while copying request info to clipboard")
        }
    };

    const handleFeedbackSubmit = async (sentiment: string, comments?: string) => {
        onFeedbackSubmitted(conversationId, request_id, sentiment, comments);
        setIsFeedbackSubmitted(true);
    };

    useEffect(() => {
        const handleLinkClick = (event: MouseEvent) => {
            event.preventDefault();
            const anchorElement = event.currentTarget as HTMLAnchorElement;
            const { path, sourcePath, pageNumber } = anchorElement.dataset;

            if (path && sourcePath && pageNumber) {
                onCitationClicked(path, sourcePath, pageNumber);
            }
        };

        // Function to add event listeners to all current .supContainer elements
        const addEventListeners = () => {
            const links = document.querySelectorAll(".supContainer");
            links.forEach(link => {
                link.addEventListener("click", handleLinkClick as EventListener);
            });
        };

        // Add event listeners initially
        addEventListeners();

        // Create a MutationObserver to watch for changes to the DOM
        const observer = new MutationObserver(() => {
            // When the DOM changes, remove and re-add the event listeners
            addEventListeners();
        });

        // Start observing the document with the configured parameters
        observer.observe(document, { childList: true, subtree: true });

        // Clean up event listeners and stop observing when the component unmounts
        return () => {
            const links = document.querySelectorAll(".supContainer");
            links.forEach(link => {
                link.removeEventListener("click", handleLinkClick as EventListener);
            });
            observer.disconnect();
        };
    }, []);

    return (
        <Stack className={`${useDarkMode ? styles.answerContainerDark : styles.answerContainer} ${isSelected ? styles.selected : ""}`}
            verticalAlign="space-between">
            <Stack.Item>
                <Stack horizontal horizontalAlign="space-between">
                    <AnswerIcon useDarkMode={useDarkMode} />
                    <div>
                        <IconButton
                            iconProps={{ iconName: "Lightbulb" }}
                            title="Show thought process"
                            ariaLabel="Show thought process"
                            onClick={() => onThoughtProcessClicked()}
                            disabled={!answer.context.thoughts?.length}
                        />
                        <IconButton
                            iconProps={{ iconName: "Documentation" }}
                            title="Show supporting content"
                            ariaLabel="Show supporting content"
                            onClick={() => onSupportingContentClicked()}
                            disabled={!answer.context.data_points?.length}
                        />
                    </div>
                </Stack>
            </Stack.Item>

            <Stack.Item grow>
                <MarkdownRenderer
                    isStreaming={isStreaming}
                    content={parsedAnswer.answerHtml}
                    useDarkMode={useDarkMode}
                />
            </Stack.Item>

            {!!citationLinks.length && (
                <Stack.Item>
                    <Stack horizontal wrap tokens={{ childrenGap: 5 }}>
                        <span className={styles.citationLearnMore}>Citations:</span>
                        {citationLinks.map((link, i) => {
                            const { title, onClick, label } = link;
                            return (
                                <a key={i} className={useDarkMode ? styles.citationDark : styles.citation} title={title} onClick={onClick}>
                                    {label}
                                </a>
                            );
                        })}
                    </Stack>
                </Stack.Item>
            )}

            {!!followupQuestions?.length && showFollowupQuestions && onFollowupQuestionClicked && (
                <Stack.Item>
                    <Stack horizontal wrap className={`${!!followupQuestions.length ? styles.followupQuestionsList : ""}`} tokens={{ childrenGap: 6 }}>
                        <span className={styles.followupQuestionLearnMore}>Follow-up questions:</span>
                        {followupQuestions.map((x, i) => {
                            return (
                                <a key={i} className={useDarkMode ? styles.followupQuestionDark : styles.followupQuestion} title={x} onClick={() => onFollowupQuestionClicked(x)}>
                                    {`${x}`}
                                </a>
                            );
                        })}
                    </Stack>
                </Stack.Item>
            )}

            <Stack.Item>
                <div className={`${styles.answerTextRequestId} ${isRequestCopied ? styles.disabled : ""}`}
                    onClick={() => onCopyRequestIdClick(`Conversation ID: ${conversationId}\nRequest ID: ${request_id}`)}>
                    <IconButton
                        iconProps={{ iconName: "Copy" }}
                        title="Copy Request ID"
                        ariaLabel="Copy Request ID"
                    />
                    <span>
                        {isRequestCopied ? "Copied!" : "Copy Request ID"}
                    </span>
                </div>
            </Stack.Item>
            <Stack.Item align="center">
                <AnswerOptionsPanel
                    onSettingsClick={onSettingsClick}
                    onRegenerateClick={onRegenerateClick}
                    onExportClick={onExportClick}
                    onCopyAnswerClick={onCopyAnswerClick}
                    onFeedbackSubmitted={(s, f) => handleFeedbackSubmit(s, f)}
                    toggleAudioPlay={toggleAudioPlay}
                    isAudioLoading={isAudioLoading}
                    isAudioPlaying={isAudioPlaying}
                    isFeedbackSubmitted={isFeedbackSubmitted}
                    isStreaming={isStreaming}
                    useDarkMode={useDarkMode}
                />
            </Stack.Item>
        </Stack>
    );
};